import { createTheme } from '@mui/material/styles';

export const BKFS_COLORS = {
    Brand: {
        Primary: {
            Lightest: "#BBA9E8",
            Light: "#8564D5",
            Middle: "#7753D0",
            Dark: "#6B4BBB",
            Darkest: "#47327D",
            TextColor: "white"
        },
        Secondary: {
            Lightest: "#80BAEC",
            Light: "#1A82DD",
            Middle: "#0074D9",
            Dark: "#0068C3",
            Darkest: "#004682",
            TextColor: "white"
        },
        Tertiary1: {
            Lightest: "#80D3FF",
            Light: "#1AAFFF",
            Middle: "#00A6FF",
            Dark: "#0095E6",
            Darkest: "#006499",
            TextColor: "black"
        },
        Tertiary2: {
            Lightest: "#A4A6BF",
            Light: "#5A5E8C",
            Middle: "#484C7F",
            Dark: "#414472",
            Darkest: "#2B2E4C",
            TextColor: "white"
        },
    },
    Status: {
        Success: {
            Lightest: "#80C593",
            Light: "#1A963D",
            Middle: "#008A27",
            Dark: "#007c23",
            Darkest: "#005317",
            TextColor: "white"
        },
        Warning: {
            Lightest: "#F8BA84",
            Light: "#F28322",
            Middle: "#F07509",
            Dark: "#D86908",
            Darkest: "#904605",
            TextColor: "black"
        },
        Danger: {
            Lightest: "#ED9B96",
            Light: "#DF4B41",
            Middle: "#DB372C",
            Dark: "#C53228",
            Darkest: "#83211A",
            TextColor: "white"
        },
        Disabled: {
            Lightest: "#BFBFBF",
            Light: "#8C8C8C",
            Middle: "#7F7F7F",
            Dark: "#727272",
            Darkest: "#4C4C4C",
            TextColor: "white"
        },
    },
    Accent: {
        Indigo: {
            Lightest: "#A6A8D6",
            Light: "#5E62B4",
            Middle: "#4C50AC",
            Dark: "#44489B",
            Darkest: "#2E3067",
            TextColor: "white"
        },
        LightGreen: {
            Lightest: "#97E6A0",
            Light: "#43D153",
            Middle: "#2ECC40",
            Dark: "#29B83A",
            Darkest: "#1C7A26",
            TextColor: "black"
        },
        Yellow: {
            Lightest: "#FFEE80",
            Light: "#FFE01A",
            Middle: "#FFDC00",
            Dark: "#E6C600",
            Darkest: "#998400",
            TextColor: "black"
        },
        Teal: {
            Lightest: "#81CBD3",
            Light: "#1CA1AF",
            Middle: "#0396A6",
            Dark: "#038795",
            Darkest: "#025A64",
            TextColor: "white"
        }
    },
    Neutral: {
        OffWhite: { Color: "#F8F8F8", TextColor: "black" },
        LightestGray: { Color: "#EFEFEF", TextColor: "black" },
        LightGray: { Color: "#CCCCCC", TextColor: "black" },
        Gray: { Color: "#999999", TextColor: "black" },
        DarkGray: { Color: "#7F7F7F", TextColor: "white" },
        DarkestGray: { Color: "#525252", TextColor: "white" },
        Charcoal: { Color: "#333333", TextColor: "white" },
    }
};
export const BKFS_NUMBER_OPTIONS = (max, min, decimalPlaces, allowEmpty) => {
    return {
        alwaysAllowDecimalCharacter: true,
        createLocalList: false,
        decimalPlacesRawValue: decimalPlaces,
        decimalPlacesShownOnBlur: decimalPlaces,
        decimalPlacesShownOnFocus: decimalPlaces,
        emptyInputBehavior: allowEmpty ? "null" : "zero",
        formulaMode: false,
        historySize: 5,
        maximumValue: max ?? 1000000000000,
        minimumValue: min ?? -1000000000000,
        modifyValueOnWheel: false,
        negativeBracketsTypeOnBlur: "(,)",
        negativeSignCharacter: "−",
        onInvalidPaste: "clamp",
        outputFormat: "number",
        selectNumberOnly: false
    }
};
const BKFS_FOUNDATION_THEME = createTheme({
    typography: {
        fontFamily: "proxima-nova;",
        fontSize: 14,
        htmlFontSize: 10,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        h1: {
            //margin: '.75rem',
            fontSize: '32px',
            fontWeight: 700,
            lineHeight: '40px'
        },
        h2: {
            //margin: 0,
            //padding: '0.75em 1em',
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '35px'
        },
        h3: {
            fontSize: '24px',
            fontWeight: 700,
            lineHeight: '30px'
        },
        h4: {
            fontSize: '21px',
            fontWeight: 700,
            lineHeight: '36px'
        },
        h5: {
            fontSize: '18px',
            fontWeight: 700,
            lineHeight: '22px'
        },
        h6: {
            fontSize: '16px',
            fontWeight: 700,
            lineHeight: '20px'
        },
        subtitle1: {
            fontSize: '18px',
            lineHeight: 1.125,
            fontWeight: 400
        },
        subtitle2: {
            fontSize: '15px',
            lineHeight: 1.125,
            fontWeight: 400
        },
        body1: {
            fontSize: '14px',
            lineHeight: 1.125,
            fontWeight: 400
        },
        body2: {
            fontSize: '13px',
            lineHeight: 1.125,
            fontWeight: 400
        },
        title: {
            margin: 0,
            padding: '0.75em 1em',
            width: '100%',
            fontSize: '1.2em',
            fontWeight: 'normal'
        },
        pageHeader: {
            fontSize: '26px',
            fontWeight: 700,
            lineHeight: '40px',
            letterSpacing: 0,
            paddingLeft: '12px'
        }
    },
    overrides: {
        MuiAlert: {
            message: {
                width: '100%',
                position: 'relative'
            },
            root: {
                height: '36px',
                maxHeight: '36px',
                paddingTop: 0,
                paddingBottom: 0,
                paddingRight: 0
            },
            standardSuccess: {
                border: "1px solid " + BKFS_COLORS.Status.Success.Middle,
                borderRadius: '2px',
                height: "36px",
                minHeight: '36px',
            },
            standardInfo: {
                backgroundColor: "#e0f4ff",
                border: "1px solid " + BKFS_COLORS.Brand.Tertiary1.Middle,
                borderRadius: '2px',
                height: "36px",
                minHeight: '36px'
            },
            standardWarning: {
                border: "1px solid " + BKFS_COLORS.Status.Warning.Middle,
                borderRadius: '2px',
                height: "36px",
                minHeight: '36px'
            },
            standardError: {
                border: "1px solid " + BKFS_COLORS.Status.Danger.Middle,
                borderRadius: '2px',
                height: "36px",
                minHeight: '36px'
            },
        },
        MuiButton: {
            label: {
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '1px'
            }
        },
        MuiIconButton: {
            root: {
                padding: '0px'
            }
        },
        MuiCardHeader: {
            root: {
                borderBottom: "1px " + BKFS_COLORS.Status.Disabled.Light + " solid"
            }
        },
        MuiCheckbox: {
            indeterminate: {
                color: BKFS_COLORS.Brand.Primary.Middle
            }
        },
        MuiChip: {
            variants: [
                {
                    props: { variant: 'showcount' },
                    style: {
                        padding: '0.5em 1em',
                        background: 'rgb(248, 246, 253)',
                        borderRadius: '2px',
                        color: '#7753D0',
                        fontSize: '0.7em',
                        fontWeight: 500
                    }
                }
            ]
        },
        MuiDialog: {
            paper: {
                padding: '24px'
            }
        },
        MuiDialogActions: {
            root: {
                justifyContent: 'flex-start',
                padding: '8px 0 24px',
                marginBottom: '-24px'
            }
        },
        MuiDialogTitle: {
            root: {
                alignItems: 'center',
                display: 'flex',
                margin: '-24px -24px 24px',
                padding: '16px 24px',
                position: 'relative'
            }
        },
        MuiDivider: {
            root: {
                color: BKFS_COLORS.Status.Disabled.Middle
            },
            light: {
                color: BKFS_COLORS.Status.Disabled.Lightest
            }
        },
        MuiExpansionPanel: {
            root: {
                "&$expanded": {
                    marginTop: 0,
                    marginBottom: 0
                }
            }
        },
        MuiExpansionPanelSummary: {
            content: {
                marginTop: 0,
                marginBottom: 0,
                "&$expanded": {
                    marginTop: 0,
                    marginBottom: 0
                }
            }
        },
        MuiFormLabel: {
            root: {
                fontSize: '14px',
                backgroundColor: 'white'
            }
        },
        MuiInputBase: {
            root: {
                lineHeight: 1.125,
                fontSize: '14px'
            }
        },
        MuiInputLabel: {
            outlined: {
                transform: "translate(14px, 14px) scale(1)",
                "& $disabled": {
                    backgroundColor: 'white'
                }
            }
        },
        MuiListItemIcon: {
            root: {
                minWidth: '30px'
            }
        },
        MuiOutlinedInput: {
            root: {
                paddingLeft: '10px',
                paddingRight: '10px',
                paddingTop: '0px',
                paddingBottom: '0px',
                "&:hover $notchedOutline": {
                    borderColor: BKFS_COLORS.Brand.Primary.Middle
                },
                '&$disabled $notchedOutline': {
                    borderStyle: 'dashed'
                }
            },
            input: {
                paddingLeft: '0px',
                paddingRight: '0px',
                paddingTop: '13px',
                paddingBottom: '13px',
            }
        },
        MuiPickersDay: {
            daySelected: {
                backgroundColor: BKFS_COLORS.Brand.Secondary.Middle,
                color: "white",
                '&:hover': {
                    // IE 11 doesn't support hex + alpha, but Chrome and others do - so we have to use rgba() instead here
                    backgroundColor: "rgba(0,116,217,0.2)"
                }
            },
            day: {
                '&:hover': {
                    // IE 11 doesn't support hex + alpha, but Chrome and others do - so we have to use rgba() instead here
                    backgroundColor: "rgba(0,116,217,0.2)"
                },
            },
            current: {
                border: '1.5px solid ' + BKFS_COLORS.Brand.Secondary.Middle,
                color: 'inherit'
            }
        },
        MuiStepIcon: {
            root: {
                '&$completed': {
                    color: BKFS_COLORS.Status.Success.Middle
                }
            },
        },
        MuiTabs: {
            indicator: {
                backgroundColor: BKFS_COLORS.Accent.Yellow.Middle
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontSize: '0.9em'
                }
            }
        },
        MuiTooltip: {
            tooltip: {
                // IE 11 doesn't support hex + alpha, but Chrome and others do - so we have to use rgba() instead here
                backgroundColor: "rgba(153, 153, 153, 0.9)"
            },
            arrow: {
                // IE 11 doesn't support hex + alpha, but Chrome and others do - so we have to use rgba() instead here
                color: "rgba(153, 153, 153, 0.9)"
            }
        }
    },
    spacing: 4,
    palette: {
        action: {
            disabled: BKFS_COLORS.Status.Disabled.Middle,
            hover: BKFS_COLORS.Brand.Primary.Middle,
        },
        primary: {
            light: BKFS_COLORS.Brand.Primary.Light,
            main: BKFS_COLORS.Brand.Primary.Middle,
            dark: BKFS_COLORS.Brand.Primary.Dark,
        },
        secondary: {
            light: BKFS_COLORS.Brand.Secondary.Light,
            main: BKFS_COLORS.Brand.Secondary.Middle,
            dark: BKFS_COLORS.Brand.Secondary.Dark,
        },
        error: {
            light: BKFS_COLORS.Status.Danger.Light,
            main: BKFS_COLORS.Status.Danger.Middle,
            dark: BKFS_COLORS.Status.Danger.Dark,
        },
        warning: {
            light: BKFS_COLORS.Status.Warning.Light,
            main: BKFS_COLORS.Status.Warning.Middle,
            dark: BKFS_COLORS.Status.Warning.Dark,
        },
        success: {
            light: BKFS_COLORS.Status.Success.Light,
            main: BKFS_COLORS.Status.Success.Middle,
            dark: BKFS_COLORS.Status.Success.Dark,
        }
    }
});

export default BKFS_FOUNDATION_THEME;